import React, { useState } from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
    Navigation,
    Autoplay,
    A11y,
    Virtual,
    EffectCoverflow,
} from 'swiper/modules';
import type { Swiper as SwiperCore } from 'swiper';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';

import BlockWrapper from '@/layouts/BlockWrapper/BlockWrapper.component';
import ContentWrapper, {
    ContentColumn,
} from '@/layouts/ContentWrapper/ContentWrapper.component';
import Heading from '@/elements/Heading/Heading.component';
import Text from '@/elements/Text/Text.component';
import Image from '@/elements/Image/Image.component';
import Button from '@/elements/Button/Button.component';
import {
    IProps as IButtonProps,
    AnchorProps,
} from '@/elements/Button/Button.interface';
import Icon from '@/elements/Icon/Icon.component';
import { IProps } from './ImageCarouselBlock.interface';
import './ImageCarouselBlock.styles.css';

export const ImageCarouselBlock: React.FC<IProps> = ({
    title,
    content,
    items,
    className,
    slidesPerViewDesktop = 4,
    slidesPerViewTablet = 2,
    slidesPerViewMobile = 1,
    spaceBetween = 20,
    loop = true,
    autoplay = false,
    autoplayDelay = 3000,
    ...blockWrapperProps
}) => {
    const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);

    const handleImageClick = (buttonProps?: IButtonProps) => {
        if (buttonProps && 'href' in buttonProps && buttonProps.href) {
            window.location.href = (buttonProps as AnchorProps).href;
        }
    };

    return (
        <BlockWrapper
            className={clsx('imageCarouselBlock', className)}
            {...blockWrapperProps}
        >
            <ContentWrapper>
                <ContentColumn>
                    <div className="imageCarouselBlock__header">
                        {title && (
                            <Heading
                                level={2}
                                className="imageCarouselBlock__heading"
                                html={title}
                            />
                        )}

                        {items.length > 0 ? (
                            <div className="imageCarouselBlock__navigation">
                                <Button
                                    className="imageCarouselBlock__navButton imageCarouselBlock__navButton--prev"
                                    aria-label="Previous image"
                                    onClick={() => swiperInstance?.slidePrev()}
                                    disabled={!swiperInstance}
                                >
                                    <Icon icon="chevron-left" />
                                </Button>
                                <Button
                                    className="imageCarouselBlock__navButton imageCarouselBlock__navButton--next"
                                    aria-label="Next image"
                                    onClick={() => swiperInstance?.slideNext()}
                                    disabled={!swiperInstance}
                                >
                                    <Icon icon="chevron-right" />
                                </Button>
                            </div>
                        ) : null}
                    </div>
                    {content && (
                        <Text className="imageCarouselBlock__content" html={content} />
                    )}
                </ContentColumn>
            </ContentWrapper>
            {items.length > 0 ? (
                <div className="imageCarouselBlock__swiperContainer">
                    <Swiper
                        className="imageCarouselBlock__swiper"
                        modules={[Navigation, Autoplay, A11y]}
                        loop={loop}
                        autoplay={
                            autoplay
                                ? {
                                    delay: autoplayDelay,
                                    disableOnInteraction: false,
                                    pauseOnMouseEnter: true,
                                }
                                : false
                        }
                        slidesPerView={slidesPerViewMobile}
                        spaceBetween={spaceBetween}
                        breakpoints={{
                            768: {
                                slidesPerView: slidesPerViewTablet,
                                spaceBetween: spaceBetween,
                            },
                            1024: {
                                slidesPerView: slidesPerViewDesktop,
                                spaceBetween: spaceBetween,
                            },
                        }}
                        a11y={{ enabled: true }}
                        onSwiper={setSwiperInstance}
                    >
                        {items.map((item, index) => {
                            const { image, button } = item;

                            return (
                                <SwiperSlide
                                    key={`image-carousel-slide-${index}`}
                                    className="imageCarouselBlock__slide"
                                    onClick={() => handleImageClick(item.button)}
                                > {
                                        button && 'href' in button && button.href ? (
                                            <a href={button.href} className="imageCarouselBlock__slideLink">
                                                {image && (
                                                    <Image
                                                        {...image}
                                                        className="imageCarouselBlock__image"
                                                        rounded={true}
                                                    />
                                                )}
                                            </a>
                                        ) : (
                                            image && (
                                                <Image
                                                    {...image}
                                                    className="imageCarouselBlock__image"
                                                    rounded={true}
                                                />
                                            ))
                                    }

                                    {button && (
                                        <Button
                                            {...button}
                                            className="imageCarouselBlock__slideButton"
                                        />
                                    )}
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </div>
            ) : (
                <Text
                    className="imageCarouselBlock__noItems"
                    html="No images available."
                />
            )}
            {items.length > 0 ? (
                <div className="imageCarouselBlock__navigation--mobile">
                    <Button
                        className="imageCarouselBlock__navButton imageCarouselBlock__navButton--prev"
                        aria-label="Previous image"
                        onClick={() => swiperInstance?.slidePrev()}
                        disabled={!swiperInstance}
                    >
                        <Icon icon="chevron-left" />
                    </Button>
                    <Button
                        className="imageCarouselBlock__navButton imageCarouselBlock__navButton--next"
                        aria-label="Next image"
                        onClick={() => swiperInstance?.slideNext()}
                        disabled={!swiperInstance}
                    >
                        <Icon icon="chevron-right" />
                    </Button>
                </div>
            ) : null}
        </BlockWrapper>
    );
};
